import { ColumnDef, ColumnSourceType, SegmentAggregates } from 'src/models/segment-cdt/SegmentCdtTypes';

/**
 * The GL-to-segment reconciliation for one period, derived from the aggregates already fetched for the
 * totals band. No extra request: the server reports `unallocatedAmount = glAggregate - aggregatedTotal`,
 * so the GL figure is recoverable as `aggregatedTotal + unallocatedAmount` and a column ties exactly when
 * its unallocated amount is zero.
 */

/** Per-column outcome. `NOT_PULLED` means no GL figure exists yet, which is not the same as a gap of zero. */
export enum ReconciliationStatus {
    TIED = 'TIED',
    UNTIED = 'UNTIED',
    NOT_PULLED = 'NOT_PULLED',
}

export interface ReconciliationEntry {
    columnId: string;
    label: string;
    /** Absent when the column has no GL aggregate yet. Never defaulted to 0 - that would invent a balance. */
    glAggregate?: number;
    /** Absent when the server withheld the column's total (e.g. a value that could not be read as a number). */
    segmentSum?: number;
    unallocated?: number;
    status: ReconciliationStatus;
}

/** Overall banner state. `PENDING` = nothing reconciled yet, which is neither a pass nor a failure. */
export enum ReconciliationVerdict {
    TIED = 'TIED',
    UNTIED = 'UNTIED',
    PENDING = 'PENDING',
}

/**
 * Whether a column can carry a GL balance at all, mirroring the backend's own rule
 * (`SegmentAggregatesComponent.unallocatedApplies`): only a pulled or derived-balance column has a GL
 * amount to differ from. A `MANUAL` column is never populated by a pull, and a `DERIVED_PCT` column is a
 * percentage with no balance behind it, so neither belongs on a reconciliation.
 */
export function isGlBackedColumn(column: ColumnDef): boolean {
    return column.sourceType === ColumnSourceType.PULL_GL
        || column.sourceType === ColumnSourceType.DERIVED_BAL;
}

/**
 * One entry per GL-backed column, in the table's own column order.
 *
 * Eligibility comes from `sourceType`, not from what the payload happens to contain, so a column that has
 * had no pull yet is still listed (as `NOT_PULLED`) rather than vanishing. That matters: a silently missing
 * row is indistinguishable from a column the feature forgot about.
 *
 * A column is `NOT_PULLED` when either figure is absent. Absence is the server's not-applicable marker, so
 * neither side is defaulted to zero: reporting `GL:0 | Seg:0 | Tied` would assert a reconciliation that was
 * never performed.
 */
export function buildReconciliationEntries(
    aggregates: SegmentAggregates | undefined,
    columns: ColumnDef[],
): ReconciliationEntry[] {
    const totals = aggregates?.aggregatedTotal;
    const unallocatedByColumn = aggregates?.unallocatedAmount;
    const entries: ReconciliationEntry[] = [];
    (columns ?? []).forEach((column) => {
        const columnId = column.columnId;
        if (!columnId || !isGlBackedColumn(column)) {
            return;
        }
        const label = column.displayName ?? columnId;
        const segmentSum = totals?.[columnId];
        const unallocated = unallocatedByColumn?.[columnId];
        if (segmentSum == null || unallocated == null) {
            entries.push({
                columnId,
                label,
                // Carried through when known, so a pulled-but-unreconciled column still shows its own sum.
                segmentSum: segmentSum ?? undefined,
                status: ReconciliationStatus.NOT_PULLED,
            });
            return;
        }
        entries.push({
            columnId,
            label,
            glAggregate: segmentSum + unallocated,
            segmentSum,
            unallocated,
            status: unallocated === 0 ? ReconciliationStatus.TIED : ReconciliationStatus.UNTIED,
        });
    });
    return entries;
}

/**
 * The banner state. A single untied column outweighs any number of tied ones, because the gap is the thing
 * worth acting on. Otherwise the period is only `TIED` once every eligible column has actually been
 * reconciled - a mix of tied and not-yet-pulled columns is still `PENDING`, since calling that "tied" would
 * overstate what has been checked.
 */
export function reconciliationVerdict(entries: ReconciliationEntry[]): ReconciliationVerdict {
    if (!entries.length) {
        return ReconciliationVerdict.PENDING;
    }
    if (entries.some((e) => e.status === ReconciliationStatus.UNTIED)) {
        return ReconciliationVerdict.UNTIED;
    }
    if (entries.every((e) => e.status === ReconciliationStatus.TIED)) {
        return ReconciliationVerdict.TIED;
    }
    return ReconciliationVerdict.PENDING;
}
